import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ArrowUpRightIcon, ChevronDownIcon, CheckCircle2Icon, HeartIcon } from 'lucide-react';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { OrgLogo } from './shared/OrgLogo';
const NAVY = '#0b1f3f';
const BLUE = '#1565C0';
const RED = '#cf142b';
const EASE = [0.23, 1, 0.32, 1] as const;
interface NetworkOrganizationsProps {
  isEs: boolean;
}

/**
 * Expandable spotlight list for organizations that have supplied a logo.
 * Collapsed, each row shows only the logo, name, and location; expanding
 * reveals the mission, leadership, and where the organization works.
 */
export function NetworkOrganizations({
  isEs
}: NetworkOrganizationsProps) {
  const orgs = SISTER_ORGANIZATIONS.filter(o => o.logo || o.mission);
  const [open, setOpen] = useState<string | null>(orgs[0]?.name ?? null);
  if (orgs.length === 0) return null;
  return <ul className="flex flex-col gap-4" data-id="element-3051" data-mp="3comcvviieo8f">
      {orgs.map(org => {
      const isOpen = open === org.name;
      const panelId = `org-panel-${org.name.replace(/\s+/g, '-')}`;
      return <li key={org.name} className="rounded-2xl border bg-white overflow-hidden" style={{
        borderColor: 'rgba(11,31,63,0.14)'
      }} data-id="element-3052" data-mp="25xexqzp1bzvv">
            {org.registered501c3 !== false && <div className="flex items-center gap-1.5 px-5 sm:px-6 py-2 border-b" style={{
          backgroundColor: 'rgba(22,163,74,0.07)',
          borderColor: 'rgba(11,31,63,0.1)'
        }} data-id="element-3053" data-mp="1i6j9gcjsfmtd">
                <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" style={{
            color: '#16a34a'
          }} aria-hidden="true" data-id="element-3054" data-mp="1r4v0gfokgmmc" />
                <span className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em]" style={{
            color: '#15803d'
          }} data-id="element-3055" data-mp="yg64sw6a59hz">
                  {isEs ? 'Organización 501(c)(3) Registrada' : 'Registered 501(c)(3)'}
                </span>
              </div>}

            <button type="button" onClick={() => setOpen(isOpen ? null : org.name)} aria-expanded={isOpen} aria-controls={panelId} className="w-full flex items-center gap-5 p-5 sm:p-6 text-left transition-colors duration-200 hover:bg-[#f5f8fc]" data-id="element-3056" data-mp="2fj1ae015b2pu">
              {org.logo ? <OrgLogo src={org.logo} alt={`${org.name} logo`} crop={org.logoCrop} scale={org.logoScale} filter={org.logoFilter} className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl bg-white" lazy data-id="element-3057" data-mp="1wtl93ahmz68d" /> : <div aria-hidden="true" className="w-32 h-20 sm:w-56 sm:h-28 flex-shrink-0 rounded-xl flex items-center justify-center font-bebas text-3xl sm:text-4xl tracking-[0.04em] text-white" style={{
            backgroundColor: org.accent
          }} data-id="element-3058" data-mp="33c9n9j6nooed">
                  {monogram(org.name)}
                </div>}
              <div className="min-w-0 flex-1" data-id="element-3059" data-mp="36s31kkiqqusu">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5" style={{
              color: BLUE
            }} data-id="element-3060" data-mp="3ugvro0com8fj">
                  {isEs ? org.focusEs : org.focus}
                </p>
                <h3 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] leading-tight mb-1.5" style={{
              color: NAVY
            }} data-id="element-3061" data-mp="3krc78q9bm6ik">
                  {org.name}
                </h3>
                <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
              color: 'rgba(11,31,63,0.55)'
            }} data-id="element-3062" data-mp="m1lsb006fhbg">
                  <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                color: RED
              }} data-id="element-3063" data-mp="2pjtog2hi4spl" />
                  {org.location}
                  {org.serviceArea ? ` · ${isEs ? org.serviceAreaEs : org.serviceArea}` : ''}
                </p>
              </div>
              <ChevronDownIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200" style={{
            color: NAVY,
            transform: isOpen ? 'rotate(180deg)' : 'none'
          }} aria-hidden="true" data-id="element-3064" data-mp="3d8mlk29v16ls" />
            </button>

            <AnimatePresence initial={false} data-id="element-3065" data-mp="2yifdmghhb5zc">
              {isOpen ? <motion.div id={panelId} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.26,
            ease: EASE
          }} className="overflow-hidden" data-id="element-3066" data-mp="3w0yoor8c8bwz">
                  <div className="border-t" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3067" data-mp="ccc7xe8agj48">
                    <div className="grid md:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-3068" data-mp="hd9r9u51bj7b">
                      {/* Mission + where they work */}
                      <div className="px-5 sm:px-7 py-7" data-id="element-3069" data-mp="qc4uqygp2f6x">
                        <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                    color: 'rgba(11,31,63,0.4)'
                  }} data-id="element-3070" data-mp="qajzt7qgwu8j">
                          {isEs ? 'Misión' : 'Mission'}
                        </p>
                        <p className="font-nunito text-[17px] leading-[1.65] mb-7" style={{
                    color: 'rgba(11,31,63,0.82)'
                  }} data-id="element-3071" data-mp="14zpx2q5qwads">
                          {isEs ? org.missionEs || org.descriptionEs : org.mission || org.description}
                        </p>

                        {org.partnerSites && org.partnerSites.length > 0 ? <div className="pt-6 border-t" style={{
                    borderColor: 'rgba(11,31,63,0.1)'
                  }} data-id="element-3072" data-mp="3a3sw0y2i5gb5">
                            <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                      color: 'rgba(11,31,63,0.4)'
                    }} data-id="element-3073" data-mp="3judpk73bjun7">
                              {isEs ? 'Su trabajo en Honduras' : 'Their work in Honduras'}
                            </p>
                            <ul className="flex flex-col gap-2" data-id="element-3074" data-mp="1zherpvh5t8i0">
                              {org.partnerSites.map(site => <li key={site.name} className="inline-flex items-center gap-2 font-nunito text-sm font-bold" style={{
                        color: NAVY
                      }} data-id="element-3075" data-mp="1tcqq9vfr8rrh">
                                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" style={{
                          color: RED
                        }} data-id="element-3076" data-mp="15ne660iw2zae" />
                                  {site.location}
                                </li>)}
                            </ul>
                          </div> : null}
                      </div>

                      {/* Leadership panel */}
                      {org.director ? <div className="px-5 sm:px-7 py-7 border-t md:border-t-0 md:border-l flex flex-col" style={{
                  borderColor: 'rgba(11,31,63,0.1)',
                  backgroundColor: '#f6f9fc'
                }} data-id="element-3077" data-mp="2vyskl5k3l3nb">
                          <div className="flex items-center gap-5" data-id="element-3078" data-mp="3us3ezcg2mb4p">
                            {/* Constant portrait frame — every organization's
                                leadership photo renders at the same size and
                                crop regardless of the source image. */}
                            <div className="w-44 h-56 sm:w-52 sm:h-64 flex-shrink-0 rounded-lg overflow-hidden bg-white p-2" style={{
                      border: '1px solid rgba(11,31,63,0.16)',
                      boxShadow: '0 1px 3px rgba(11,31,63,0.08)'
                    }} data-id="element-3079" data-mp="3gx1c6y6it1w4">
                              <img src={org.director.photo} alt={org.director.name || `${org.name} ${org.director.title}`} loading="lazy" decoding="async" className="w-full h-full rounded object-cover object-top" data-id="element-3080" data-mp="160sp7jme8l4g" />
                            </div>
                            <div className="min-w-0" data-id="element-3081" data-mp="2a1iobe1i5bi7">
                              {org.director.name ? <p className="font-bebas text-2xl tracking-[0.03em] leading-tight" style={{
                        color: NAVY
                      }} data-id="element-3082" data-mp="2e1azzzm6zxrs">
                                  {org.director.name}
                                </p> : null}
                              <p className="font-nunito text-[11px] font-bold uppercase tracking-[0.16em] mt-1" style={{
                        color: BLUE
                      }} data-id="element-3083" data-mp="20ogj8f6v7l8z">
                                {isEs ? org.director.titleEs : org.director.title}
                              </p>
                              {org.director.linkedin ? <a href={org.director.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3084" data-mp="1hrp5sme080cu">
                                  LinkedIn
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3085" data-mp="vhp6jqngvcnb" />
                                </a> : null}
                              {org.director.profileUrl ? <a href={org.director.profileUrl} className="flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-3086" data-mp="mntwq5begy1c">
                                  {isEs ? 'Ver Perfil' : 'View Profile'}
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3087" data-mp="3uwan7izmvf86" />
                                </a> : null}
                            </div>
                          </div>
                        </div> : null}
                    </div>

                    {/* Actions — matched buttons across the full width */}
                    {org.donateUrl || org.url ? <div className="px-5 sm:px-7 py-5 border-t flex flex-col sm:flex-row gap-3" style={{
                borderColor: 'rgba(11,31,63,0.1)'
              }} data-id="element-3088" data-mp="2h0j2h2pgk4wl">
                        {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: RED
                }} data-id="element-3089" data-mp="285uuh4si0943">
                            {isEs ? 'Visitar sitio web' : 'Visit Website'}
                            <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3090" data-mp="1fkv2c79a844q" />
                          </a> : null}
                        {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: '#4FA8E8'
                }} data-id="element-3091" data-mp="12zmo0zpe10gm">
                            <HeartIcon className="w-3.5 h-3.5" data-id="element-3092" data-mp="y2d05ohncwuw" />
                            {isEs ? 'Donar / Participar' : 'Donate / Get Involved'}
                          </a> : null}
                      </div> : null}
                  </div>
                </motion.div> : null}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}

/** Two-letter stand-in shown until an organization supplies its logo. */
function monogram(name: string): string {
  // Acronym-led names ("ASJ — …", "CRC …") use the acronym itself.
  const first = name.split(/\s/)[0];
  if (/^[A-Z]{2,4}$/.test(first)) return first;
  const words = name.split(/[\s—-]+/).filter(w => /^[A-Za-zÀ-ÿ]/.test(w) && !['de', 'for', 'a', 'the'].includes(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
}